import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, ScreenRouter } from '../../managers';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { CB } from '../../combat/spriteConfig';
import { elementColor } from '../../utils/elementColors';
import { formatElement, formatTier } from '../components/GameTerms';
import type { CrawlDetails, CampaignData } from '../../types';

type CrawlType = 'daily' | 'event' | 'free' | 'campaign';

export class CrawlDetailScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'crawl-detail',
    name: 'Crawl Details',
    persistent: false,
    needsPhaser: false,
  };

  private crawlId: string | null = null;
  private crawlType: CrawlType = 'daily';
  private details: CrawlDetails | null = null;
  private starting = false;

  constructor() {
    super({ rootId: 'crawl-detail-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'crawl-detail-root';
    root.innerHTML = `
      <div class="crawl-detail-screen">
        ${screenHeaderHTML('Crawl')}
        <div class="cd-body">
          <div class="cd-loading">Loading crawl details...</div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {}

  protected setupSubscriptions(): void {}

  async onEnter(params?: Record<string, unknown>): Promise<void> {
    this.crawlId = (params?.crawlId as string) ?? null;
    this.crawlType = (params?.crawlType as CrawlType) ?? 'daily';
    this.details = null;
    this.starting = false;

    if (!this.crawlId) {
      ScreenRouter.back();
      return;
    }

    this.onRender();
    await this.fetchDetails();
  }

  protected onRender(): void {
    if (this.rootEl) renderScreenCurrencies(this.rootEl, 'crawl-detail');
    this.renderContent();
  }

  private async fetchDetails(): Promise<void> {
    if (!this.crawlId) return;

    const result = await APIManager.getCrawlDetails(this.crawlId);
    if (!result.success || !result.data) {
      console.error('[CrawlDetail] Failed to fetch details:', result.error);
      const body = this.$('.cd-body');
      if (body) body.innerHTML = '<div class="cd-loading">Failed to load crawl details</div>';
      return;
    }

    this.details = result.data;
    this.onRender();
  }

  /** Check if this is a campaign crawl the player hasn't completed yet */
  private isCampaignFirstPlay(): boolean {
    if (this.crawlType !== 'campaign') return false;
    const campaignData = StateManager.get('campaignData') as CampaignData | undefined;
    const stepInfo = campaignData?.steps.find(s => s.crawlId === this.crawlId);
    return stepInfo?.status === 'available';
  }

  /** Build an element hint from the first half of encounters, adding "and more..." if later rooms introduce new elements */
  private buildElementHint(): string {
    if (!this.details) return '';
    const { encounters } = this.details;
    const midpoint = Math.ceil(encounters.length / 2);
    const earlyElements = new Set<string>();
    const lateElements = new Set<string>();

    encounters.forEach((enc, i) => {
      for (const enemy of enc.enemies) {
        const types = enemy.defensiveTypes ?? (enemy.element ? [enemy.element] : []);
        for (const t of types) {
          if (i < midpoint) earlyElements.add(t);
          else lateElements.add(t);
        }
      }
    });

    const displayed = [...earlyElements];
    const hasHidden = [...lateElements].some(el => !earlyElements.has(el));
    const hint = displayed.map(el => {
      const color = elementColor(el);
      return `<span class="cd-element-badge" style="color:${color}">${formatElement(el)}</span>`;
    }).join(' ');

    return hint + (hasHidden ? ' <span class="cd-element-more">and more...</span>' : '');
  }

  private renderContent(): void {
    if (!this.details) return;

    const { config, encounters, rewards } = this.details;
    const isFree = this.crawlType === 'free';
    const campaignFirstPlay = this.isCampaignFirstPlay();

    // Header
    const titleEl = this.$('.screen-title');
    if (titleEl) titleEl.textContent = config.name;

    const body = this.$('.cd-body');
    if (!body) return;

    // Overview
    const roomCount = config.encounters.length;
    const tierCounts = { trash: 0, elite: 0, boss: 0 };
    for (const enc of config.encounters) {
      tierCounts[enc.tier as keyof typeof tierCounts]++;
    }
    const tierSummary = [
      tierCounts.trash > 0 ? `${tierCounts.trash} Trash` : '',
      tierCounts.elite > 0 ? `${tierCounts.elite} Elite` : '',
      tierCounts.boss > 0 ? `${tierCounts.boss} Boss` : '',
    ].filter(Boolean).join(' \u00B7 ');

    // Encounters section — abbreviated for campaign first play, full otherwise
    let encounterSectionHTML = '';
    if (campaignFirstPlay) {
      const campaignData = StateManager.get('campaignData') as CampaignData | undefined;
      const stepInfo = campaignData?.steps.find(s => s.crawlId === this.crawlId);
      const desc = stepInfo?.description ?? '';
      const elementHint = this.buildElementHint();

      encounterSectionHTML = `
        <div class="cd-campaign-briefing">
          <div class="cd-section-title">Briefing</div>
          <div class="cd-briefing-desc">${desc}</div>
          <div class="cd-briefing-elements">
            <span class="cd-briefing-label">Elements:</span>
            ${elementHint}
          </div>
        </div>
      `;
    } else {
      const encountersHTML = encounters.map(enc => {
        const tierClass = `cd-tier-${enc.tier}`;
        const tierLabel = formatTier(enc.tier);

        const enemiesHTML = enc.enemies.map(enemy => {
          const types = enemy.defensiveTypes ?? (enemy.element ? [enemy.element] : []);
          const badges = types.map(t => {
            const eColor = elementColor(t);
            return `<span class="cd-element-badge" style="color:${eColor}">${formatElement(t)}</span>`;
          }).join(' / ');
          return `
            <div class="cd-enemy-row">
              <span class="cd-enemy-name">${enemy.name}</span>
              ${badges}
            </div>
          `;
        }).join('');

        return `
          <div class="cd-encounter-card ${tierClass}">
            <div class="cd-encounter-header">
              <span class="cd-encounter-name">${enc.name}</span>
              <span class="cd-tier-badge ${tierClass}">${tierLabel}</span>
            </div>
            <div class="cd-enemy-list">${enemiesHTML}</div>
          </div>
        `;
      }).join('');

      encounterSectionHTML = `
        <div class="cd-section-title">Encounters</div>
        <div class="cd-encounters">${encountersHTML}</div>
      `;
    }

    // Rewards HTML — simplified: completion bonus, level, drop category tags
    let rewardsHTML = `
      <div class="cd-rewards-section">
        <div class="cd-section-title">Rewards</div>
        <div class="cd-reward-row">
          <span class="cd-reward-label">Completion</span>
          <span class="cd-reward-value">${rewards.completionXP} XP \u00B7 ${rewards.completionGold} Gold</span>
        </div>
        <div class="cd-reward-row">
          <span class="cd-reward-label">Drop Level</span>
          <span class="cd-reward-value">${rewards.dropItemLevel}</span>
        </div>
    `;

    // Show drop categories as tags
    const dropTags: string[] = [];
    if (rewards.possibleUnits.length > 0) dropTags.push('Strands');
    if (rewards.possibleItems.length > 0) dropTags.push('Items');
    if (rewards.currencyDrops.length > 0) dropTags.push('Currency');

    if (dropTags.length > 0) {
      const tagsHTML = dropTags.map(t => `<span class="cd-drop-tag">${t}</span>`).join('');
      rewardsHTML += `
        <div class="cd-reward-row">
          <span class="cd-reward-label">Possible Drops</span>
          <div class="cd-reward-value cd-drop-tags">${tagsHTML}</div>
        </div>
      `;
    }

    if (isFree) {
      rewardsHTML += `<div class="cd-manual-note">Free crawls award reduced XP, gold, and a single random drop.</div>`;
    }

    rewardsHTML += '</div>';

    // Banner — show for ALL crawl types, with the appropriate label
    let bannerHTML = '';
    if (config.bgKey) {
      const imgPath = `${import.meta.env.BASE_URL}sprites/processed/bg/${config.bgKey}.webp${CB}`;
      let label = '';
      if (this.crawlType === 'campaign') {
        const campaignData = StateManager.get('campaignData') as CampaignData | undefined;
        const stepInfo = campaignData?.steps.find(s => s.crawlId === this.crawlId);
        label = stepInfo ? `Step ${stepInfo.stepIndex + 1}` : '';
      } else if (this.crawlType === 'event') {
        label = 'Event Crawl';
      } else if (this.crawlType === 'daily') {
        label = 'Daily Crawl';
      } else if (this.crawlType === 'free') {
        label = 'Free Crawl';
      }
      bannerHTML = `
        <div class="cd-campaign-banner">
          <img src="${imgPath}" alt="" class="cd-campaign-banner-bg" />
          <div class="cd-campaign-banner-gradient"></div>
          <div class="cd-campaign-banner-text">
            ${label ? `<span class="cd-campaign-banner-step">${label}</span>` : ''}
            <span class="cd-campaign-banner-name">${config.name}</span>
          </div>
        </div>
      `;
    }

    // Zone modifiers strip
    let modHTML = '';
    const mods = config.zoneModifiers;
    if (mods && mods.length > 0) {
      const MODIFIER_INFO: Record<string, { label: string; desc: string }> = {
        smothered: { label: 'Smothered', desc: '-50% Healing' },
        draining: { label: 'Draining', desc: '+15% Focus Costs' },
        fortified: { label: 'Fortified', desc: '+50% Monster Life' },
      };
      const pills = mods.map(m => {
        const info = MODIFIER_INFO[m] ?? { label: m, desc: '' };
        return `<span class="cd-zone-mod-pill">${info.label} <span class="cd-zone-mod-desc">${info.desc}</span></span>`;
      }).join('');
      modHTML = `<div class="cd-zone-mods">${pills}</div>`;
    }

    body.innerHTML = `
      ${bannerHTML}
      <div class="cd-overview">
        <span class="cd-overview-rooms">${roomCount} Encounters</span>
        <span class="cd-overview-tiers">${tierSummary}</span>
        <span class="cd-overview-ilvl">Lv ${rewards.dropItemLevel}</span>
      </div>
      ${modHTML}
      ${encounterSectionHTML}
      ${rewardsHTML}
      <div class="cd-action">
        <button class="cd-back-btn" type="button">Back</button>
        <button class="cd-begin-btn" type="button">Begin Crawl</button>
      </div>
    `;

    // Bind action buttons
    this.$('.cd-back-btn')?.addEventListener('click', () => ScreenRouter.back());
    this.$('.cd-begin-btn')?.addEventListener('click', () => this.beginCrawl());
  }

  private beginCrawl(): void {
    if (this.starting || !this.crawlId || !this.details) return;
    ScreenRouter.navigate('hero-select', {
      crawlId: this.crawlId,
      crawlType: this.crawlType,
      crawlDetails: this.details,
    });
  }
}
